/**
 * PrintPreview - Preview component for the Closing Disclosure print output
 *
 * Displays the CD form in a print-optimized layout with print controls.
 */

import * as React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { usePrint } from '../hooks/usePrint';
import type { ClosingDisclosureData } from '../types/closingDisclosureData';
import { CDPage1, CDPage2, CDPage3, CDPage4, CDPage5 } from './pages';

// Import print styles
import '../styles/print.css';

export interface PrintPreviewProps {
  /** Form data to preview */
  data: Partial<ClosingDisclosureData>;
  /** Form ID for footer */
  formId?: string;
  /** Total number of pages */
  totalPages?: number;
  /** Callback when close button is clicked */
  onClose?: () => void;
  /** Document title for print */
  documentTitle?: string;
  /** Additional className */
  className?: string;
}

/**
 * PrintPreview - Displays CD in print-ready format with controls
 */
export function PrintPreview({
  data,
  formId = 'CFPB Form H-25(A)',
  totalPages = 5,
  onClose,
  documentTitle = 'Closing Disclosure',
  className,
}: PrintPreviewProps) {
  const { print, isPrinting, openPrintPreview } = usePrint({
    documentTitle,
  });

  // Reference to the printable content
  const contentRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className={cn('cd-print-preview', className)}>
      {/* Toolbar */}
      <div className="cd-print-preview-toolbar cd-no-print">
        <h1 className="cd-print-preview-title">Print Preview</h1>
        <div className="cd-print-preview-actions">
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          )}
          <Button variant="outline" onClick={openPrintPreview}>
            Open in New Window
          </Button>
          <Button onClick={print} disabled={isPrinting}>
            {isPrinting ? 'Printing...' : 'Print'}
          </Button>
        </div>
      </div>

      {/* Printable Content */}
      <div ref={contentRef} className="cd-print-preview-container cd-print-preview-content">
        {/* Page 1 */}
        <div className="cd-print-preview-page">
          <span className="cd-print-preview-page-number cd-no-print">Page 1 of {totalPages}</span>
          <CDPage1
            data={data.page1 || {}}
            totalPages={totalPages}
            formId={formId}
            editable={false}
          />
        </div>

        {/* Page 2 */}
        <div className="cd-print-preview-page">
          <span className="cd-print-preview-page-number cd-no-print">Page 2 of {totalPages}</span>
          <CDPage2
            data={data.page2 || {}}
            totalPages={totalPages}
            formId={formId}
            editable={false}
          />
        </div>

        {/* Page 3 */}
        <div className="cd-print-preview-page">
          <span className="cd-print-preview-page-number cd-no-print">Page 3 of {totalPages}</span>
          <CDPage3
            data={data.page3 || {}}
            totalPages={totalPages}
            formId={formId}
            editable={false}
          />
        </div>

        {/* Page 4 */}
        <div className="cd-print-preview-page">
          <span className="cd-print-preview-page-number cd-no-print">Page 4 of {totalPages}</span>
          <CDPage4
            data={data.page4 || {}}
            totalPages={totalPages}
            formId={formId}
            editable={false}
          />
        </div>

        {/* Page 5 */}
        <div className="cd-print-preview-page">
          <span className="cd-print-preview-page-number cd-no-print">Page 5 of {totalPages}</span>
          <CDPage5
            data={data.page5 || {}}
            totalPages={totalPages}
            formId={formId}
            editable={false}
          />
        </div>
      </div>
    </div>
  );
}

PrintPreview.displayName = 'PrintPreview';

/**
 * PrintButton - Simple button that triggers print
 */
export interface PrintButtonProps {
  /** Document title for print */
  documentTitle?: string;
  /** Button variant */
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link';
  /** Button size */
  size?: 'default' | 'sm' | 'lg' | 'icon';
  /** Button label */
  children?: React.ReactNode;
  /** Additional className */
  className?: string;
}

export function PrintButton({
  documentTitle,
  variant = 'default',
  size = 'default',
  children = 'Print',
  className,
}: PrintButtonProps) {
  const { print, isPrinting } = usePrint({ documentTitle });

  return (
    <Button
      variant={variant}
      size={size}
      onClick={print}
      disabled={isPrinting}
      className={className}
    >
      {isPrinting ? 'Printing...' : children}
    </Button>
  );
}

PrintButton.displayName = 'PrintButton';
